/**
 * Knowledge API
 * API calls for managing agent knowledge base documents
 * Uses userApiClient for /v1/agents endpoints
 */

import userApiClient from './userClient';

export interface KnowledgeDocument {
  doc_id: string;
  content: string;
  agent_type: string;
  metadata?: Record<string, any>;
  created_at?: string;
  relevance_score?: number;
}

export interface DocumentListResponse {
  documents: KnowledgeDocument[];
  total: number;
  agent_type: string;
  limit: number;
  offset: number;
}

export interface DocumentSearchResponse {
  documents: KnowledgeDocument[];
  query: string;
  total_found: number;
  agent_type: string;
}

export interface DocumentDeleteResponse {
  status: string;
  doc_id?: string;
  deleted_count?: number;
  message: string;
}

export interface DocumentQueryOptions {
  limit?: number;
  offset?: number;
}

export const knowledgeApi = {
  /**
   * List all documents in the knowledge base
   */
  list: async (
    agentType: string,
    options: DocumentQueryOptions = {}
  ): Promise<DocumentListResponse> => {
    const { limit = 50, offset = 0 } = options;
    const { data } = await userApiClient.get(`/agents/${agentType}/knowledge`, {
      params: { limit, offset },
    });
    return data;
  },

  /**
   * Get a specific document by ID
   */
  get: async (agentType: string, docId: string): Promise<KnowledgeDocument> => {
    const { data } = await userApiClient.get(`/agents/${agentType}/knowledge/${docId}`);
    return data;
  },

  /**
   * Create a new document
   */
  create: async (
    agentType: string,
    content: string,
    metadata?: Record<string, any>
  ): Promise<KnowledgeDocument> => {
    const { data } = await userApiClient.post(`/agents/${agentType}/knowledge`, {
      content,
      metadata,
    });
    return data;
  },

  /**
   * Update an existing document
   */
  update: async (
    agentType: string,
    docId: string,
    content: string,
    metadata?: Record<string, any>
  ): Promise<KnowledgeDocument> => {
    const { data } = await userApiClient.put(`/agents/${agentType}/knowledge/${docId}`, {
      content,
      metadata,
    });
    return data;
  },

  /**
   * Delete a document
   */
  delete: async (agentType: string, docId: string): Promise<DocumentDeleteResponse> => {
    const { data } = await userApiClient.delete(`/agents/${agentType}/knowledge/${docId}`);
    return data;
  },

  /**
   * Clear all documents from knowledge base
   */
  clear: async (agentType: string): Promise<DocumentDeleteResponse> => {
    const { data } = await userApiClient.delete(`/agents/${agentType}/knowledge`);
    return data;
  },

  /**
   * Search documents in knowledge base
   */
  search: async (
    agentType: string,
    query: string,
    limit: number = 5
  ): Promise<DocumentSearchResponse> => {
    const { data } = await userApiClient.post(`/agents/${agentType}/knowledge/search`, {
      query,
      limit,
    });
    return data;
  },

  /**
   * Upload a file to knowledge base
   */
  uploadFile: async (
    agentType: string,
    file: File,
    name?: string,
    description?: string,
    metadata?: Record<string, any>
  ): Promise<KnowledgeDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    if (name) formData.append('name', name);
    if (description) formData.append('description', description);
    if (metadata) formData.append('metadata', JSON.stringify(metadata));

    const { data } = await userApiClient.post(
      `/agents/${agentType}/knowledge/from-file`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data;
  },

  /**
   * Import document from URL
   */
  importFromUrl: async (
    agentType: string,
    url: string,
    name?: string,
    description?: string,
    metadata?: Record<string, any>
  ): Promise<KnowledgeDocument> => {
    const { data } = await userApiClient.post(`/agents/${agentType}/knowledge/from-url`, {
      url,
      name,
      description,
      metadata,
    });
    return data;
  },
};

export default knowledgeApi;
